import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  DollarSign, 
  CreditCard,
  Building,
  Calendar,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { serenityClient } from '../services/serenityClient';
import type { Debtor, Case, PaymentPlan, Payment } from '../types';

interface PaymentPageProps {
  debtor: Debtor;
}

type PaymentMethod = 'card' | 'bank_transfer' | 'direct_debit';

// Interest rates by installment count (from CollectionCriteria table)
const INSTALLMENT_RATES: Record<number, { rate: number; label: string }> = {
  3: { rate: 0, label: '0% - No financing fee' },
  6: { rate: 5, label: '5% financing fee' },
  12: { rate: 10, label: '10% financing fee' },
  24: { rate: 18, label: '18% financing fee' },
  36: { rate: 25, label: '25% financing fee' },
};

export default function PaymentPage({ debtor }: PaymentPageProps) {
  const { caseId } = useParams<{ caseId: string }>();
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  
  // Payment form state
  const [paymentType, setPaymentType] = useState<'full' | 'partial' | 'plan'>('full');
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  
  // Plan form state
  const [planFrequency, setPlanFrequency] = useState<'weekly' | 'bi_weekly' | 'monthly'>('monthly');
  const [planInstallments, setPlanInstallments] = useState<number>(6);

  useEffect(() => {
    loadPaymentData();
  }, [caseId, debtor.debtor_id]);

  const loadPaymentData = async () => {
    setIsLoading(true);
    try {
      // Load all cases for debtor
      const casesResult = await serenityClient.queryData<Case>(
        `SELECT * FROM Cases WHERE debtor_id = '${debtor.debtor_id}' AND status NOT IN ('paid_in_full', 'settled', 'write_off') ORDER BY total_amount DESC`
      );
      setCases(casesResult);

      // If specific case requested, select it
      if (caseId) {
        const targetCase = casesResult.find(c => c.case_id === caseId);
        if (targetCase) {
          setSelectedCase(targetCase);
          setPaymentAmount(targetCase.total_amount - (targetCase.amount_paid || 0));
          
          // Load payment plan for this case
          const planResult = await serenityClient.queryData<PaymentPlan>(
            `SELECT * FROM PaymentPlans WHERE case_id = '${caseId}' AND status = 'active' LIMIT 1`
          );
          setPaymentPlan(planResult[0] || null);
        }
      } else if (casesResult.length > 0) {
        setSelectedCase(casesResult[0]);
        setPaymentAmount(casesResult[0].total_amount - (casesResult[0].amount_paid || 0));
      }
    } catch (error) {
      console.error('Failed to load payment data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = cases.find(c => c.case_id === e.target.value);
    setSelectedCase(selected || null);
    if (selected) {
      setPaymentAmount(selected.total_amount - (selected.amount_paid || 0));
    }
  };

  const handlePaymentTypeChange = (type: 'full' | 'partial' | 'plan') => {
    setPaymentType(type);
    if (type === 'full' && selectedCase) {
      setPaymentAmount(selectedCase.total_amount - (selectedCase.amount_paid || 0));
    }
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCase) return;

    setIsProcessing(true);
    
    try {
      // Simulate payment gateway processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (paymentType === 'plan') {
        // Create payment plan
        const planId = `PLN-${Date.now()}`;
        const startDate = new Date();
        const nextPaymentDate = new Date(startDate);
        
        // Calculate next payment date based on frequency
        if (planFrequency === 'weekly') {
          nextPaymentDate.setDate(nextPaymentDate.getDate() + 7);
        } else if (planFrequency === 'bi_weekly') {
          nextPaymentDate.setDate(nextPaymentDate.getDate() + 14);
        } else {
          nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
        }

        const remainingBalance = selectedCase.total_amount - (selectedCase.amount_paid || 0);
        const rate = INSTALLMENT_RATES[planInstallments]?.rate || 0;
        const fee = remainingBalance * (rate / 100);
        const totalAmount = remainingBalance + fee;
        const installmentAmount = totalAmount / planInstallments;

        const newPlan: Partial<PaymentPlan> = {
          plan_id: planId,
          case_id: selectedCase.case_id,
          debtor_id: debtor.debtor_id,
          plan_type: 'installment',
          total_amount: totalAmount,
          original_amount: remainingBalance,
          financing_rate: rate,
          financing_fee: fee,
          installment_amount: installmentAmount,
          installment_frequency: planFrequency,
          num_installments: planInstallments,
          installments_remaining: planInstallments,
          start_date: startDate.toISOString(),
          next_payment_date: nextPaymentDate.toISOString(),
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        await serenityClient.insertData('PaymentPlans', newPlan);
        console.log('Payment plan created:', planId);
        
        // Create automatic workflow task for payment plan validation
        try {
          const taskId = `TASK-${Date.now()}`;
          const now = new Date().toISOString();
          await serenityClient.insertData('WorkflowTasks', {
            task_id: taskId,
            workflow_type: 'payment_monitoring',
            entity_type: 'payment_plan',
            entity_id: planId,
            case_id: selectedCase.case_id,
            debtor_id: debtor.debtor_id,
            task_type: 'payment_plan_validation',
            title: 'Payment Plan Validation',
            description: `Validate payment plan: ${planInstallments} installments of ${formatCurrency(installmentAmount)}`,
            status: 'pending',
            priority: 'normal',
            assigned_role: 'collections_agent',
            created_by: 'system',
            ai_agent_code: 'dfs-validador',
            requires_approval: false,
            created_at: now,
            updated_at: now,
          });
          console.log('[Workflow] Created payment plan validation task:', taskId);
        } catch (taskError) {
          console.warn('Failed to create workflow task:', taskError);
        }
      } else {
        // Record payment
        const paymentId = `PAY-${Date.now()}`;
        const referenceNumber = `REF-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
        
        const newPayment: Partial<Payment> = {
          payment_id: paymentId,
          case_id: selectedCase.case_id,
          debtor_id: debtor.debtor_id,
          amount: paymentAmount,
          currency: 'EUR',
          payment_date: new Date().toISOString(),
          payment_method: paymentMethod,
          status: 'completed',
          payment_reference: referenceNumber,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        await serenityClient.insertData('Payments', newPayment);
        console.log('Payment recorded:', paymentId, 'Reference:', referenceNumber);
      }

      setPaymentComplete(true);
    } catch (error) {
      console.error('Payment failed:', error);
      alert('Payment processing failed. Please try again or contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {paymentType === 'plan' ? 'Payment Plan Created!' : 'Payment Successful!'}
          </h2>
          <p className="text-gray-500 mb-6">
            {paymentType === 'plan' 
              ? `Your payment plan of ${planInstallments} installments has been set up successfully.`
              : `Your payment of ${formatCurrency(paymentAmount)} has been processed.`
            }
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/dashboard"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </Link>
            {selectedCase && (
              <Link
                to={`/case/${selectedCase.case_id}`}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                View Case
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (cases.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Outstanding Cases</h2>
          <p className="text-gray-500 mb-4">You don't have any active cases requiring payment.</p>
          <Link
            to="/dashboard"
            className="text-blue-600 hover:underline flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const remainingBalance = selectedCase ? selectedCase.total_amount - (selectedCase.amount_paid || 0) : 0;
  
  // Calculate total with financing fee
  const financingRate = INSTALLMENT_RATES[planInstallments]?.rate || 0;
  const financingFee = remainingBalance * (financingRate / 100);
  const totalWithFinancing = remainingBalance + financingFee;
  const installmentAmount = totalWithFinancing / planInstallments;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to={caseId ? `/case/${caseId}` : '/dashboard'}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Make a Payment</h1>
                <p className="text-xs text-gray-500">Secure payment processing</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmitPayment} className="space-y-6">
          {/* Case Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Case</h2>
            <select
              value={selectedCase?.case_id || ''}
              onChange={handleCaseChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {cases.map((c) => (
                <option key={c.case_id} value={c.case_id}>
                  {c.case_id} - {c.origin} - {formatCurrency(c.total_amount - (c.amount_paid || 0), c.currency)} remaining
                </option>
              ))}
            </select>

            {selectedCase && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Debt</p>
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(selectedCase.total_amount, selectedCase.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Remaining Balance</p>
                    <p className="font-semibold text-red-600">
                      {formatCurrency(remainingBalance, selectedCase.currency)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {paymentPlan && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Active Payment Plan</p>
                  <p className="text-sm text-blue-700">
                    You have an active payment plan: {formatCurrency(paymentPlan.installment_amount || 0)} {paymentPlan.installment_frequency}.
                    Next payment due: {paymentPlan.next_payment_date ? new Date(paymentPlan.next_payment_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Payment Type */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Type</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => handlePaymentTypeChange('full')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  paymentType === 'full' 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <DollarSign className={`w-6 h-6 mb-2 ${paymentType === 'full' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-semibold ${paymentType === 'full' ? 'text-blue-900' : 'text-gray-900'}`}>Pay in Full</p>
                <p className="text-sm text-gray-500">Clear your balance completely</p>
              </button>
              
              <button
                type="button"
                onClick={() => handlePaymentTypeChange('partial')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  paymentType === 'partial' 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <CreditCard className={`w-6 h-6 mb-2 ${paymentType === 'partial' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-semibold ${paymentType === 'partial' ? 'text-blue-900' : 'text-gray-900'}`}>Partial Payment</p>
                <p className="text-sm text-gray-500">Pay a custom amount</p>
              </button>
              
              <button
                type="button"
                onClick={() => handlePaymentTypeChange('plan')}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  paymentType === 'plan' 
                    ? 'border-blue-600 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Calendar className={`w-6 h-6 mb-2 ${paymentType === 'plan' ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`font-semibold ${paymentType === 'plan' ? 'text-blue-900' : 'text-gray-900'}`}>Payment Plan</p>
                <p className="text-sm text-gray-500">Split into installments</p>
              </button>
            </div>
          </div>

          {/* Payment Amount (for partial) */}
          {paymentType === 'partial' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Amount</h2>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                <input
                  type="number"
                  min="1"
                  max={remainingBalance}
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Maximum: {formatCurrency(remainingBalance)}
              </p>
            </div>
          )}

          {/* Payment Plan Options */}
          {paymentType === 'plan' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
                  <select
                    value={planFrequency}
                    onChange={(e) => setPlanFrequency(e.target.value as 'weekly' | 'bi_weekly' | 'monthly')}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="bi_weekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Installments</label>
                  <select
                    value={planInstallments}
                    onChange={(e) => setPlanInstallments(parseInt(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {Object.entries(INSTALLMENT_RATES).map(([num, { label }]) => (
                      <option key={num} value={num}>
                        {num} installments - {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Financing breakdown */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Outstanding balance:</span>
                    <span className="font-medium">{formatCurrency(remainingBalance)}</span>
                  </div>
                  {financingRate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Financing fee ({financingRate}%):</span>
                      <span className="font-medium text-amber-600">+{formatCurrency(financingFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                    <span className="text-gray-600">Total to pay:</span>
                    <span className="font-semibold">{formatCurrency(totalWithFinancing)}</span>
                  </div>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">Your installment will be:</p>
                  <p className="text-2xl font-bold text-green-800">
                    {formatCurrency(installmentAmount)} / {planFrequency.replace('_', '-')}
                  </p>
                  {financingRate === 0 && (
                    <p className="text-xs text-green-600 mt-1">✨ Interest-free financing!</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Payment Method (for immediate payments) */}
          {paymentType !== 'plan' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Method</h2>
              <div className="space-y-3">
                <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === 'card' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <CreditCard className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Credit/Debit Card</p>
                    <p className="text-sm text-gray-500">Visa, Mastercard, Amex</p>
                  </div>
                </label>
                
                <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  paymentMethod === 'bank_transfer' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="bank_transfer"
                    checked={paymentMethod === 'bank_transfer'}
                    onChange={() => setPaymentMethod('bank_transfer')}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <Building className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Bank Transfer</p>
                    <p className="text-sm text-gray-500">Direct bank payment</p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Summary & Submit */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>
            <div className="space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-500">Case</span>
                <span className="font-medium text-gray-900">{selectedCase?.case_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment Type</span>
                <span className="font-medium text-gray-900">
                  {paymentType === 'full' ? 'Full Payment' : paymentType === 'partial' ? 'Partial Payment' : 'Payment Plan'}
                </span>
              </div>
              {paymentType !== 'plan' && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-bold text-gray-900">{formatCurrency(paymentAmount)}</span>
                </div>
              )}
              {paymentType === 'plan' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Installments</span>
                    <span className="font-medium text-gray-900">{planInstallments}x {formatCurrency(installmentAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Frequency</span>
                    <span className="font-medium text-gray-900">{planFrequency.replace('_', '-')}</span>
                  </div>
                </>
              )}
            </div>

            <button
              type="submit"
              disabled={isProcessing || !selectedCase || (paymentType !== 'plan' && paymentAmount <= 0)}
              className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {paymentType === 'plan' ? 'Set Up Payment Plan' : `Pay ${formatCurrency(paymentAmount)}`}
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              Your payment is secured with 256-bit SSL encryption.
            </p>
          </div>
        </form>
      </main>
    </div>
  );
}
